// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import { ProductDto } from "@insite/client-framework/Types/ApiModels";
import { Draft } from "immer";

export type ShopByLookState = {
    isLoading: { [erp: string]: boolean };
    byErp: { [erp: string]: { product: ProductDto; productInfo: ProductInfo } };
    isErpsLoading: boolean;
    productsByErps: any;
    productsInfo: { [erp: string]: ProductInfo };
};

const initialState: ShopByLookState = {
    isLoading: {},
    byErp: {},
    isErpsLoading: false,
    productsByErps: [],
    productsInfo: {},
};

const shopByLook = createTypedReducerWithImmer(initialState, {
    "ShopByLook/BeginLoadingProduct": (draft: Draft<ShopByLookState>, actions: { productErp: string }) => {
        draft.isLoading[actions.productErp] = true;
    },
    "ShopByLook/CompleteLoadingProduct": (draft: Draft<ShopByLookState>, actions: { productErp: string }) => {
        delete draft.isLoading[actions.productErp];
    },
    "ShopByLook/AddProductData": (draft: Draft<ShopByLookState>, actions: { product: ProductDto }) => {
        draft.byErp[actions.product.erpNumber] = {
            ...draft.byErp[actions.product.erpNumber],
            product: actions.product,
        };
    },
    "ShopByLook/AddProductInfo": (
        draft: Draft<ShopByLookState>,
        actions: { productInfo: ProductInfo; productErp: string },
    ) => {
        draft.byErp[actions.productErp] = {
            ...draft.byErp[actions.productErp],
            productInfo: actions.productInfo,
        };
    },
    "ShopByLook/AddProductsInfo": (draft: Draft<ShopByLookState>, actions: { productsInfo: any }) => {
        draft.productsInfo = { ...draft.productsInfo, ...actions.productsInfo };
    },
    "ShopByLook/UpdateQty": (draft: Draft<ShopByLookState>, actions: { qty: number; productErp: string }) => {
        draft.byErp[actions.productErp].productInfo.qtyOrdered = actions.qty;
    },
    "ShopByLook/UpdateUom": (draft: Draft<ShopByLookState>, actions: { uom: string; productErp: string }) => {
        draft.byErp[actions.productErp].productInfo.unitOfMeasure = actions.uom;
    },
    "ShopByLook/erpsIsLoading": (draft: Draft<ShopByLookState>) => {
        draft.isErpsLoading = true;
    },
    "ShopByLook/resetShopByLook": (draft: Draft<ShopByLookState>) => {
        draft.productsByErps = [];
        draft.productsInfo = {};
        draft.byErp = {};
    },
    "ShopByLook/ProductsByErps": (draft: Draft<ShopByLookState>, actions: { productsByErps: any }) => {
        draft.isErpsLoading = false;
        draft.productsByErps = [...draft.productsByErps, ...actions.productsByErps];
    },
    "ShopByLook/UpdatePageProductQty": (
        draft: Draft<ShopByLookState>,
        actions: { qtyOrdered: number; productErp: string },
    ) => {
        draft.productsInfo[actions.productErp].qtyOrdered = actions.qtyOrdered;
    },
    "ShopByLook/UpdatePageProductUom": (
        draft: Draft<ShopByLookState>,
        actions: { uom: string; productErp: string },
    ) => {
        draft.productsInfo[actions.productErp].unitOfMeasure = actions.uom;
    },
});

(reducers as any).shopByLook = shopByLook;

type shopByLookReducers = {
    shopByLook: typeof shopByLook;
};

export type shopByLookActions = Parameters<shopByLookReducers[keyof shopByLookReducers]>[1] | AnyAction;
